<template>
  <div class="section">
    <div class="toolbar">
      <span class="hint">
        Всего: {{ items.length }} · активных: {{ activeCount }} · неактивных: {{ items.length - activeCount }}
      </span>
      <div class="toolbar-actions">
        <button
          class="btn-secondary"
          :disabled="probeAllRunning || loading"
          @click="probeAll"
        >
          {{ probeAllRunning ? `Проверка ${probeAllProgress.done}/${probeAllProgress.total}...` : 'Проверить все токены' }}
        </button>
        <button class="btn-secondary" :disabled="loading" @click="loadAccounts">
          {{ loading ? 'Обновляем...' : 'Обновить' }}
        </button>
      </div>
    </div>

    <div v-if="probeAllRunning && probeAllProgress.current" class="info-message">
      Проверяем: <b>{{ probeAllProgress.current }}</b>
    </div>

    <div v-if="hasError" class="error-message">Не удалось загрузить ATI-аккаунты.</div>
    <div v-if="message" :class="messageType === 'error' ? 'error-message' : 'info-message'">{{ message }}</div>

    <div class="table-wrap">
      <table class="data-table">
        <thead>
          <tr>
            <th>ID контакта</th>
            <th>ID аккаунта</th>
            <th>Почта</th>
            <th class="col-bool">Активен</th>
            <th class="col-bool">Токен</th>
            <th class="col-actions">Действия</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading && items.length === 0">
            <td :colspan="6" class="empty-cell">Загрузка...</td>
          </tr>
          <tr v-else-if="items.length === 0">
            <td :colspan="6" class="empty-cell">ATI-аккаунтов не найдено</td>
          </tr>
          <template v-for="item in items" :key="item.contact_id">
            <tr>
              <td class="mono">{{ item.contact_id }}</td>
              <td class="mono">{{ item.account_id }}</td>
              <td>{{ item.employee_email || '—' }}</td>
              <td class="col-bool">
                <span class="badge" :class="item.is_active ? 'badge-on' : 'badge-off'">
                  {{ item.is_active ? 'да' : 'нет' }}
                </span>
              </td>
              <td class="col-bool">
                <span v-if="probing[item.contact_id]" class="badge badge-muted">...</span>
                <span
                  v-else-if="probeResults[item.contact_id]"
                  class="badge"
                  :class="tokenBadgeClass(item.contact_id)"
                  :title="tokenBadgeTitle(item.contact_id)"
                >
                  {{ tokenBadgeText(item.contact_id) }}
                </span>
                <span v-else class="badge badge-muted">—</span>
              </td>
              <td class="col-actions">
                <button
                  class="btn-edit"
                  :disabled="probing[item.contact_id] || saving"
                  @click="probeOne(item.contact_id)"
                >
                  {{ probing[item.contact_id] ? '...' : 'Проверить' }}
                </button>
                <button class="btn-edit" :disabled="saving" @click="startEdit(item)">Изменить</button>
              </td>
            </tr>
            <tr v-if="expandedContact === item.contact_id && probeResults[item.contact_id]" class="detail-row">
              <td :colspan="6">
                <TokenProbeResult
                  :result="probeResults[item.contact_id]"
                  :expected-contact-id="item.contact_id"
                />
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <div v-if="editTarget" class="modal-overlay" @click.self="closeEdit">
      <div class="modal">
        <h2>Редактировать аккаунт</h2>
        <div v-if="formError" class="error-message">{{ formError }}</div>
        <label class="field">
          <span>ID контакта</span>
          <input :value="editTarget.contact_id" class="modal-input" disabled>
        </label>
        <label class="field">
          <span>ID аккаунта</span>
          <input :value="editTarget.account_id" class="modal-input" disabled>
        </label>
        <label class="field">
          <span>Почта *</span>
          <input v-model="editEmail" class="modal-input" type="email" placeholder="user@example.com">
        </label>
        <label class="field-inline">
          <input type="checkbox" v-model="editActive">
          <span>Активен</span>
        </label>
        <button class="btn-token" :disabled="saving" @click="openToken(editTarget)">Заменить токен</button>
        <div class="modal-actions">
          <button class="btn-cancel" :disabled="saving" @click="closeEdit">Отмена</button>
          <button class="btn-save" :disabled="saving" @click="save">
            {{ saving ? '...' : 'Сохранить' }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="tokenTarget" class="modal-overlay" @click.self="closeToken">
      <div class="modal modal-wide">
        <h2>Заменить токен</h2>
        <p class="modal-hint">Аккаунт <b>{{ tokenTarget.employee_email }}</b> (ID аккаунта {{ tokenTarget.account_id }}).</p>
        <p class="modal-hint">api_token — write-only: его можно задать, но значение не хранится и не возвращается в UI.</p>
        <label class="field">
          <span>Новый api_token *</span>
          <input v-model="newToken" class="modal-input" type="password" placeholder="вставьте токен из ATI" autocomplete="off">
        </label>
        <button
          class="btn-probe"
          :disabled="tokenProbing || !newToken"
          @click="runTokenProbe"
        >
          {{ tokenProbing ? 'Проверяем...' : 'Проверить токен' }}
        </button>
        <p v-if="tokenProbeHint" class="modal-hint muted">{{ tokenProbeHint }}</p>
        <div v-if="tokenProbing || tokenProbe" class="probe-panel">
          <TokenProbeResult :result="tokenProbe" :expected-contact-id="tokenTarget.contact_id" />
        </div>
        <div v-if="tokenError" class="error-message">{{ tokenError }}</div>
        <div class="modal-actions">
          <button class="btn-cancel" :disabled="tokenSaving" @click="closeToken">Отмена</button>
          <button class="btn-save" :disabled="tokenSaving || !newToken" @click="saveToken">
            {{ tokenSaving ? '...' : 'Сохранить' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref, watch } from 'vue'
import { getAccounts, probeAccount, probeToken, updateAccount } from '@/backend/distributorRoutingApi'
import TokenProbeResult from './TokenProbeResult.vue'

const TOKEN_BADGE = {
  publish_ok: { text: '✓ Ок', cls: 'badge-on' },
  duplicate_ok: { text: '✓ Ок', cls: 'badge-on' },
  validated: { text: '✓В?', cls: 'badge-warn' },
  unauthorized: { text: '✕ 401', cls: 'badge-bad' },
  business_block: { text: '✕ 403', cls: 'badge-bad' },
  no_connection: { text: '⚠ связи', cls: 'badge-warn' },
  account_lookup_failed: { text: '⚠ аккаунты', cls: 'badge-warn' },
  skipped: { text: '—', cls: 'badge-muted' },
  skipped_no_token: { text: 'нет токена', cls: 'badge-muted' },
}

export default {
  name: 'AccountsSection',
  components: { TokenProbeResult },
  setup() {
    const items = ref([])
    const loading = ref(false)
    const hasError = ref(false)
    const message = ref('')
    const messageType = ref('info')
    const saving = ref(false)

    const editTarget = ref(null)
    const editEmail = ref('')
    const editActive = ref(false)
    const formError = ref('')

    const tokenTarget = ref(null)
    const newToken = ref('')
    const tokenError = ref('')
    const tokenSaving = ref(false)

    const probeResults = ref({})
    const probing = ref({})
    const expandedContact = ref(null)

    const probeAllRunning = ref(false)
    const probeAllProgress = ref({ done: 0, total: 0, current: '' })

    const tokenProbe = ref(null)
    const tokenProbing = ref(false)
    const tokenProbeHint = ref('')
    let tokenDebounce = null

    const activeCount = computed(() => items.value.filter((a) => a.is_active).length)

    const notify = (text, type = 'info') => {
      message.value = text
      messageType.value = type
    }

    const loadAccounts = async () => {
      loading.value = true
      message.value = ''
      try {
        const data = await getAccounts({ active_only: false })
        items.value = Array.isArray(data) ? data : []
        hasError.value = false
      } catch {
        hasError.value = true
      } finally {
        loading.value = false
      }
    }

    const setProbeResult = (contactId, result) => {
      probeResults.value = { ...probeResults.value, [contactId]: result }
      if (result) expandedContact.value = contactId
    }

    const probeOne = async (contactId) => {
      probing.value = { ...probing.value, [contactId]: true }
      try {
        const result = await probeAccount(contactId)
        setProbeResult(contactId, result)
      } catch (e) {
        setProbeResult(contactId, {
          token_present: true,
          contact_id: contactId,
          me: { ok: false, status: null, error: 'no_connection' },
          publish: { verdict: 'no_connection', status: null },
        })
      } finally {
        probing.value = { ...probing.value, [contactId]: false }
      }
    }

    const probeAll = async () => {
      const targets = items.value.filter((a) => a.is_active && a.contact_id != null)
      if (targets.length === 0) return
      probeAllRunning.value = true
      probeAllProgress.value = { done: 0, total: targets.length, current: '' }
      let ok = 0
      let failed = 0
      for (const item of targets) {
        probeAllProgress.value = {
          done: probeAllProgress.value.done,
          total: targets.length,
          current: item.employee_email || item.contact_id,
        }
        await probeOne(item.contact_id)
        const v = probeResults.value[item.contact_id]?.publish?.verdict
        if (v === 'publish_ok' || v === 'duplicate_ok') ok += 1
        else failed += 1
        probeAllProgress.value = { ...probeAllProgress.value, done: probeAllProgress.value.done + 1 }
      }
      probeAllRunning.value = false
      probeAllProgress.value = { done: 0, total: 0, current: '' }
      notify(`Проверено ${targets.length}: рабочих ${ok}, проблемных ${failed}.`, failed > 0 ? 'error' : 'info')
    }

    const startEdit = (item) => {
      editTarget.value = item
      editEmail.value = item.employee_email || ''
      editActive.value = !!item.is_active
      formError.value = ''
    }

    const closeEdit = () => {
      editTarget.value = null
      formError.value = ''
    }

    const save = async () => {
      const target = editTarget.value
      if (!target) return
      const email = String(editEmail.value || '').trim()
      if (!email) { formError.value = 'Заполните почту'; return }
      saving.value = true
      formError.value = ''
      try {
        await updateAccount(target.account_id, {
          employee_email: email,
          is_active: editActive.value,
        })
        target.employee_email = email
        target.is_active = editActive.value
        notify('Аккаунт обновлён.')
        closeEdit()
      } catch (e) {
        formError.value = extractError(e, 'Не удалось обновить аккаунт')
      } finally {
        saving.value = false
      }
    }

    const openToken = (item) => {
      tokenTarget.value = item
      newToken.value = ''
      tokenError.value = ''
      tokenProbe.value = null
      tokenProbeHint.value = ''
    }

    const closeToken = () => {
      tokenTarget.value = null
      newToken.value = ''
      tokenError.value = ''
      tokenProbe.value = null
      tokenProbeHint.value = ''
      if (tokenDebounce) { clearTimeout(tokenDebounce); tokenDebounce = null }
    }

    const runTokenProbe = async () => {
      const target = tokenTarget.value
      const token = String(newToken.value || '').trim()
      if (!target || !token) return
      tokenProbing.value = true
      tokenProbe.value = null
      tokenError.value = ''
      tokenProbeHint.value = 'Шаг 1: данные ATI (/me), Шаг 2: тест публикации груза с автоудалением.'
      try {
        const result = await probeToken(token, target.contact_id)
        tokenProbe.value = result
      } catch (e) {
        tokenError.value = extractError(e, 'Не удалось проверить токен')
      } finally {
        tokenProbing.value = false
      }
    }

    const saveToken = async () => {
      const target = tokenTarget.value
      const token = String(newToken.value || '').trim()
      if (!target || !token) return
      tokenSaving.value = true
      tokenError.value = ''
      try {
        await updateAccount(target.account_id, {
          employee_email: target.employee_email,
          api_token: token,
        })
        notify('Токен обновлён (write-only — значение не возвращается).')
        closeToken()
      } catch (e) {
        tokenError.value = extractError(e, 'Не удалось обновить токен')
      } finally {
        tokenSaving.value = false
      }
    }

    // Авто-проверка при вставке токена (debounce 700ms).
    watch(newToken, (val) => {
      if (tokenDebounce) clearTimeout(tokenDebounce)
      tokenDebounce = null
      tokenProbe.value = null
      const token = String(val || '').trim()
      if (!token) return
      tokenDebounce = setTimeout(() => {
        runTokenProbe()
      }, 700)
    })

    const tokenBadgeMeta = (contactId) => {
      const v = probeResults.value[contactId]?.publish?.verdict
      return v && TOKEN_BADGE[v] ? TOKEN_BADGE[v] : null
    }
    const tokenBadgeText = (contactId) => tokenBadgeMeta(contactId)?.text || '—'
    const tokenBadgeClass = (contactId) => tokenBadgeMeta(contactId)?.cls || 'badge-muted'
    const tokenBadgeTitle = (contactId) => {
      const r = probeResults.value[contactId]
      if (!r) return ''
      const parts = [`verdict: ${r.publish?.verdict}`]
      if (r.me?.ok && r.me?.account) parts.push(` ATI: ${r.me.account.FullFirmName || ''}`.trim())
      return parts.join(' · ')
    }

    const extractError = (error, fallback) => {
      if (typeof error.response?.data?.detail === 'string') return error.response.data.detail
      if (error.response?.data?.detail) return JSON.stringify(error.response.data.detail)
      return fallback
    }

    onMounted(loadAccounts)

    return {
      items, loading, hasError, activeCount,
      message, messageType, saving,
      editTarget, editEmail, editActive, formError,
      tokenTarget, newToken, tokenError, tokenSaving,
      tokenProbe, tokenProbing, tokenProbeHint,
      probeResults, probing, expandedContact,
      probeAllRunning, probeAllProgress,
      loadAccounts, startEdit, closeEdit, save,
      openToken, closeToken, saveToken,
      probeOne, probeAll, runTokenProbe,
      tokenBadgeText, tokenBadgeClass, tokenBadgeTitle,
    }
  },
}
</script>

<style scoped>
.toolbar { display: flex; justify-content: space-between; align-items: center; gap: 10px; margin-bottom: 14px; flex-wrap: wrap; }
.toolbar-actions { display: flex; gap: 8px; }
.hint { color: #475569; font-size: 13px; font-weight: 600; }
.table-wrap { overflow-x: auto; }
.data-table { border-collapse: collapse; width: 100%; font-size: 13px; min-width: 760px; }
.data-table th, .data-table td { border-bottom: 1px solid #edf0f3; padding: 8px 10px; text-align: left; vertical-align: middle; }
.data-table th { background: #f8fafc; color: #475569; font-size: 11px; font-weight: 800; white-space: nowrap; }
.data-table tbody tr:hover { background: #eef4ff; }
.detail-row { background: #f8fafc; }
.detail-row:hover { background: #f1f5f9; }
.detail-row td { padding: 10px; }
.mono { font-family: ui-monospace, monospace; font-size: 12px; }
.col-bool { text-align: center; }
.data-table th.col-actions, .data-table td.col-actions { text-align: right; white-space: nowrap; }
.col-actions button { margin-left: 4px; }
.empty-cell { text-align: center !important; color: #6b7280; padding: 24px !important; }
.badge { display: inline-block; padding: 1px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
.badge-on { background: #e6f4ea; color: #1e7e34; }
.badge-off { background: #f1f5f9; color: #6b7280; }
.badge-bad { background: #fee2e2; color: #991b1b; }
.badge-warn { background: #fef3c7; color: #92400e; }
.badge-muted { background: #f1f5f9; color: #6b7280; }
.error-message { background: #fee2e2; border: 1px solid #fecaca; border-radius: 10px; color: #991b1b; font-size: 12px; margin-bottom: 12px; padding: 10px 12px; }
.info-message { background: #eff6ff; border: 1px solid #dbeafe; border-radius: 10px; color: #1d4ed8; font-size: 12px; margin-bottom: 12px; padding: 10px 12px; }
button { border: none; border-radius: 8px; box-sizing: border-box; cursor: pointer; font-weight: 700; font-size: 12px; height: 36px; padding: 0 14px; }
button:disabled { cursor: not-allowed; opacity: 0.5; }
.btn-secondary { background: #f1f5f9; color: #475569; }
.col-actions button { height: 30px; padding: 0 10px; }
.btn-edit { background: #eef4ff; color: #1d4ed8; }
.btn-save { background: #16a34a; color: #fff; }
.btn-cancel { background: #f1f5f9; color: #475569; }
.btn-token { background: #f1f5f9; color: #475569; border: 1px solid #d1d5db; width: 100%; margin-bottom: 12px; padding: 8px 10px; }
.btn-probe { background: #eef4ff; color: #1d4ed8; width: 100%; margin-bottom: 8px; }
.modal-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.45); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal { background: #fff; border-radius: 14px; box-shadow: 0 20px 50px rgba(15,23,42,0.25); padding: 22px; width: 460px; max-width: 92vw; }
.modal-wide { width: 560px; }
.modal h2 { margin: 0 0 14px; font-size: 18px; }
.modal-hint { color: #475569; font-size: 13px; margin: 0 0 14px; line-height: 1.5; }
.modal-hint.muted { margin-bottom: 8px; }
.field { display: flex; flex-direction: column; gap: 4px; margin-bottom: 12px; font-size: 12px; color: #475569; }
.field-inline { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; font-size: 13px; color: #1f2937; }
.modal-input { background: #fff; border: 1px solid #d1d5db; border-radius: 8px; box-sizing: border-box; font-size: 13px; height: 36px; padding: 8px 10px; }
.modal-input:disabled { background: #f3f4f6; color: #9ca3af; }
.modal-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 8px; }
.probe-panel { background: #f8fafc; border: 1px solid #e5e7eb; border-radius: 10px; padding: 10px; margin-bottom: 12px; }
</style>
